import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, Package, Upload, X, ChevronLeft, ChevronRight, Eye, Tag, Minus, Plus as PlusIcon } from 'lucide-react'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import { useStoreValidation } from '../../../hooks/useStoreValidation'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import ProductImage from '../../../components/ProductImage'
import { getCache, getCacheStale, setCache, removeCache } from '../../../lib/cache'
import { showOfflineToast, showConnectionErrorToast } from '../../../lib/toastManager'

interface ProductVariant {
  id?: string
  variantName: string
  variantType: 'flavor' | 'color' | 'size' | 'other'
  stockQuantity: number
  sku?: string
  imageUrl?: string
  imageFile?: File
  imagePreview?: string
}

interface Product {
  id: string
  name: string
  description: string
  price: number
  image_url?: string
  category_id: string
  store_id: string
  is_available: boolean
  stock_quantity: number
  unit: string
  is_featured: boolean
  created_at: string
  barcode?: string
  sku?: string
  variants?: ProductVariant[]
  has_variants?: boolean
  category?: {
    name: string
    slug: string
  }
}

interface Category {
  id: string
  name: string
  slug: string
}

const StoreProducts = () => {
  const navigate = useNavigate()
  const params = useParams()
  const { user } = useAuth()
  const { activeStore } = useStore()
  const { isStoreApproved, requireApprovedStore } = useStoreValidation()
  
  // Get storeId from URL params, activeStore, or user object
  const storeId = params.storeId || activeStore?.id || user?.storeId
  
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [creatingProduct, setCreatingProduct] = useState(false)
  const isCreatingRef = useRef(false) // Ref para prevenir doble creación
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<string>('all')
  const [filterAvailability, setFilterAvailability] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const productsPerPage = 12

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    stock_quantity: '',
    unit: 'unidad',
    is_available: true,
    is_featured: false,
    barcode: ''
  })
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string>('')
  const [productSuggestions, setProductSuggestions] = useState<any[]>([])
  const [selectedGlobalProduct, setSelectedGlobalProduct] = useState<any | null>(null)
  const [searchingProducts, setSearchingProducts] = useState(false)
  const [variants, setVariants] = useState<ProductVariant[]>([])
  const [showVariantsSection, setShowVariantsSection] = useState(false)
  const [showImageSelector, setShowImageSelector] = useState(false)
  const [availableImages, setAvailableImages] = useState<Array<{ key: string; signedUrl: string; size: number }>>([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [imageSearchTerm, setImageSearchTerm] = useState('')
  const [selectedImageKey, setSelectedImageKey] = useState<string | null>(null)
  const [variantImageSelectorIndex, setVariantImageSelectorIndex] = useState<number | null>(null)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  useEffect(() => {
    if (storeId) {
      loadProducts()
      loadCategories()
    }
  }, [storeId, currentPage])

  const loadProducts = async () => {
    if (!storeId) {
      setLoading(false)
      return
    }

    const cacheKey = `products_${storeId}_${currentPage}`
    
    // Intentar cargar desde cache primero
    const cachedProducts = getCacheStale<Product[]>(cacheKey)
    if (cachedProducts && cachedProducts.length > 0) {
      setProducts(cachedProducts)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/products/store/${storeId}/products?page=${currentPage}&limit=${productsPerPage}`, {
        timeout: 30000 // 30 segundos para cargar productos
      })
      
      if (response.data && response.data.success !== false) {
        // Normalizar los productos para asegurar que price sea un número
        const normalizedProducts = (response.data.data || []).map((product: any) => ({
          ...product,
          price: Number(product.price) || 0,
          stock_quantity: Number(product.stock_quantity) || 0
        }))
        setProducts(normalizedProducts)
        setTotalPages(response.data.pagination?.totalPages || 1)
        
        // Guardar en cache (1 hora de TTL)
        setCache(cacheKey, normalizedProducts, 60 * 60 * 1000)
      } else {
        console.error('Error in response:', response.data)
        // No borrar productos si hay datos en cache
        if (!cachedProducts || cachedProducts.length === 0) {
          setProducts([])
        }
      }
    } catch (error: any) {
      console.error('Error loading products:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) {
        if (cachedProducts && cachedProducts.length > 0) {
          // No mostrar toast, es información positiva (tenemos datos en caché)
          // Ya tenemos los datos del cache, no hacer nada más
          return
        } else {
          // Solo mostrar error si realmente no hay datos
          showConnectionErrorToast('Sin conexión y no hay datos en caché.')
        }
      } else {
        toast.error(error.response?.data?.message || 'Error al cargar productos')
      }
      
      // Solo borrar productos si no hay datos en cache
      if (!cachedProducts || cachedProducts.length === 0) {
        setProducts([])
      }
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    if (!storeId) return

    try {
      const response = await api.get(`/products/store/${storeId}/categories`, {
        timeout: 30000 // 30 segundos para cargar categorías
      })
      if (response.data && response.data.success !== false) {
        setCategories(response.data.data || [])
      }
    } catch (error: any) {
      console.error('Error loading categories:', error)
      // No mostrar error para categorías, solo log
    }
  }

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = filterCategory === 'all' || product.category_id === filterCategory
    const matchesAvailability = filterAvailability === 'all' ||
                               (filterAvailability === 'available' && product.is_available) ||
                               (filterAvailability === 'unavailable' && !product.is_available)

    return matchesSearch && matchesCategory && matchesAvailability
  })

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      stock_quantity: '',
      unit: 'unidad',
      is_available: true,
      is_featured: false,
      barcode: ''
    })
    setSelectedImage(null)
    setImagePreview('')
    setSelectedImageKey(null)
    setEditingProduct(null)
    setProductSuggestions([])
    setSelectedGlobalProduct(null)
    setVariants([])
    setShowVariantsSection(false)
  }

  const addVariant = () => {
    setVariants([...variants, {
      variantName: '',
      variantType: 'flavor',
      stockQuantity: 0,
      sku: '',
      imageUrl: '',
      imagePreview: ''
    }])
  }

  const removeVariant = (index: number) => {
    setVariants(variants.filter((_, i) => i !== index))
  }

  const updateVariant = (index: number, field: keyof ProductVariant, value: any) => {
    const updated = [...variants]
    updated[index] = { ...updated[index], [field]: value }
    setVariants(updated)
  }

  const handleVariantImageSelect = (index: number, file: File) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const updated = [...variants]
      updated[index] = {
        ...updated[index],
        imageFile: file,
        imagePreview: reader.result as string
      }
      setVariants(updated)
    }
    reader.readAsDataURL(file)
  }

  const handleSelectExistingImage = (imageKey: string, signedUrl: string) => {
    setSelectedImage(null) // Limpiar archivo seleccionado
    setSelectedImageKey(imageKey) // Guardar la key de la imagen seleccionada
    setImagePreview(signedUrl)
    setShowImageSelector(false)
    toast.success('Imagen seleccionada')
  }

  const handleSelectExistingVariantImage = (imageKey: string, signedUrl: string) => {
    if (variantImageSelectorIndex !== null) {
      const updated = [...variants]
      updated[variantImageSelectorIndex] = {
        ...updated[variantImageSelectorIndex],
        imageUrl: signedUrl,
        imagePreview: signedUrl
      }
      setVariants(updated)
      setVariantImageSelectorIndex(null)
      setShowImageSelector(false)
      toast.success('Imagen de variante seleccionada')
    }
  }

  useEffect(() => {
    if (showImageSelector) {
      const loadImages = async () => {
        try {
          setLoadingImages(true)
          const response = await api.listImages('products', 100, imageSearchTerm || undefined)
          if (response.success && response.data) {
            setAvailableImages(response.data.filter((img: any) => img.signedUrl))
          }
        } catch (error) {
          console.error('Error loading images:', error)
          toast.error('Error al cargar imágenes disponibles')
        } finally {
          setLoadingImages(false)
        }
      }
      loadImages()
    } else {
      // Limpiar imágenes cuando se cierra el modal
      setAvailableImages([])
      setImageSearchTerm('')
      setVariantImageSelectorIndex(null)
    }
  }, [showImageSelector, imageSearchTerm])

  // Búsqueda inteligente de productos mientras se escribe
  useEffect(() => {
    const searchProducts = async () => {
      if (!formData.name || formData.name.length < 2 || editingProduct) {
        setProductSuggestions([])
        return
      }

      try {
        setSearchingProducts(true)
        const response = await api.searchProducts(formData.name, 5)
        if (response.success) {
          setProductSuggestions(response.data || [])
        }
      } catch (error) {
        console.error('Error searching products:', error)
        setProductSuggestions([])
      } finally {
        setSearchingProducts(false)
      }
    }

    const debounceTimer = setTimeout(searchProducts, 300)
    return () => clearTimeout(debounceTimer)
  }, [formData.name, editingProduct])


  const handleSelectProduct = (product: any) => {
    setSelectedGlobalProduct(product)
    setFormData({
      ...formData,
      name: product.name,
      description: product.description || '',
      unit: product.unit || 'unidad',
      // Mantener precio y stock vacíos para que el usuario los ingrese
    })
    if (product.image_url) {
      setImagePreview(product.image_url)
    }
    setProductSuggestions([])
  }

  const handleCreateProduct = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!requireApprovedStore('crear productos')) return
    if (!storeId) return
    
    // Prevenir múltiples envíos usando ref (más confiable que state en React StrictMode)
    if (isCreatingRef.current || creatingProduct) {
      return
    }

    isCreatingRef.current = true
    setCreatingProduct(true)
    try {
      const productData = new FormData()
      productData.append('name', formData.name)
      productData.append('description', formData.description)
      productData.append('price', formData.price)
      productData.append('categoryId', formData.category_id) // El endpoint espera categoryId
      productData.append('stockQuantity', formData.stock_quantity) // El endpoint espera stockQuantity
      productData.append('unit', formData.unit)
      productData.append('isAvailable', formData.is_available.toString()) // El endpoint espera isAvailable
      productData.append('isFeatured', formData.is_featured.toString()) // El endpoint espera isFeatured
      
      // Agregar código de barras si existe
      if (formData.barcode && formData.barcode.trim() !== '') {
        productData.append('barcode', formData.barcode.trim())
      }

      // Si hay una imagen nueva seleccionada, subirla
      if (selectedImage) {
        productData.append('image', selectedImage)
      } else if (selectedImageKey) {
        // Si se seleccionó una imagen existente, usar su key
        productData.append('imageUrl', selectedImageKey)
      }

      // Si se seleccionó un producto global, enviar su ID
      if (selectedGlobalProduct) {
        productData.append('globalProductId', selectedGlobalProduct.id)
      }

      // Agregar variantes si existen
      if (variants.length > 0) {
        // Subir imágenes de variantes si las hay
        const variantsWithImages = await Promise.all(
          variants.map(async (variant) => {
            let imageUrl = variant.imageUrl || ''
            
            // Si hay un archivo de imagen, subirlo primero
            if (variant.imageFile) {
              const variantImageData = new FormData()
              variantImageData.append('image', variant.imageFile)
              variantImageData.append('folder', 'products')
              
              try {
                const uploadResponse = await api.post('/upload/image', variantImageData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                })
                if (uploadResponse.data.success && uploadResponse.data.data?.key) {
                  imageUrl = uploadResponse.data.data.key
                }
              } catch (error) {
                console.error('Error uploading variant image:', error)
                toast.error(`Error al subir imagen de variante ${variant.variantName}`)
              }
            } else if (variant.imageUrl && variant.imageUrl.trim() !== '') {
              // Si se seleccionó una imagen existente, usar su key directamente
              imageUrl = variant.imageUrl
            }
            
            return {
              variantName: variant.variantName,
              variantType: variant.variantType,
              stockQuantity: variant.stockQuantity,
              sku: variant.sku || '',
              imageUrl: imageUrl || undefined
            }
          })
        )
        
        productData.append('variants', JSON.stringify(variantsWithImages))
      }

      // Usar el endpoint correcto con storeId en la URL
      // Aumentar timeout para operaciones con múltiples imágenes
      await api.post(`/products/store/${storeId}/products`, productData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 segundos para operaciones con múltiples imágenes
      })

      toast.success('Producto creado exitosamente')
      setShowCreateModal(false)
      resetForm()
      loadProducts()
    } catch (error: any) {
      console.error('Error creating product:', error)
      let errorMessage = 'Error al crear el producto'
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'La operación tardó demasiado. Por favor, intenta de nuevo con menos imágenes o verifica tu conexión.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
    } finally {
      setCreatingProduct(false)
      isCreatingRef.current = false
    }
  }

  const handleEditProduct = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!requireApprovedStore('editar productos')) return
    if (!editingProduct) return

    try {
      const productData = new FormData()
      productData.append('name', formData.name)
      productData.append('description', formData.description)
      productData.append('price', formData.price)
      productData.append('category_id', formData.category_id)
      productData.append('stock_quantity', formData.stock_quantity)
      productData.append('unit', formData.unit)
      productData.append('is_available', formData.is_available.toString())
      productData.append('is_featured', formData.is_featured.toString())
      
      // Agregar código de barras si existe
      if (formData.barcode && formData.barcode.trim() !== '') {
        productData.append('barcode', formData.barcode.trim())
      }

      // Si hay una imagen nueva seleccionada, subirla
      if (selectedImage) {
        productData.append('image', selectedImage)
      } else if (selectedImageKey) {
        // Si se seleccionó una imagen existente, usar su key
        productData.append('imageUrl', selectedImageKey)
      }

      await api.put(`/products/${editingProduct.id}`, productData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      // Actualizar variantes si hay cambios
      if (variants.length > 0) {
        await Promise.all(
          variants.map(async (variant) => {
            let imageUrl = variant.imageUrl || ''
            
            // Si hay un archivo de imagen nuevo, subirlo primero
            if (variant.imageFile) {
              const variantImageData = new FormData()
              variantImageData.append('image', variant.imageFile)
              variantImageData.append('folder', 'products')
              
              try {
                const uploadResponse = await api.post('/upload/image', variantImageData, {
                  headers: { 'Content-Type': 'multipart/form-data' }
                })
                if (uploadResponse.data.success && uploadResponse.data.data?.key) {
                  imageUrl = uploadResponse.data.data.key
                }
              } catch (error) {
                console.error('Error uploading variant image:', error)
                toast.error(`Error al subir imagen de variante ${variant.variantName}`)
              }
            }
            
            // Si la variante tiene ID, actualizarla; si no, crearla
            if (variant.id) {
              // Actualizar variante existente
              try {
                await api.put(`/products/${editingProduct.id}/variants/${variant.id}`, {
                  variantName: variant.variantName,
                  variantType: variant.variantType,
                  stockQuantity: variant.stockQuantity,
                  sku: variant.sku || '',
                  imageUrl: imageUrl || undefined
                })
              } catch (error) {
                console.error(`Error updating variant ${variant.variantName}:`, error)
                toast.error(`Error al actualizar variante ${variant.variantName}`)
              }
            } else {
              // Crear nueva variante
              try {
                await api.post(`/products/${editingProduct.id}/variants`, {
                  variantName: variant.variantName,
                  variantType: variant.variantType,
                  stockQuantity: variant.stockQuantity,
                  sku: variant.sku || '',
                  imageUrl: imageUrl || undefined
                })
              } catch (error) {
                console.error(`Error creating variant ${variant.variantName}:`, error)
                toast.error(`Error al crear variante ${variant.variantName}`)
              }
            }
          })
        )
      }

      toast.success('Producto actualizado exitosamente')
      setEditingProduct(null)
      resetForm()
      loadProducts()
    } catch (error) {
      console.error('Error updating product:', error)
      toast.error('Error al actualizar el producto')
    }
  }

  const handleDeleteProduct = async (productId: string) => {
    if (!requireApprovedStore('eliminar productos')) return
    if (!confirm('¿Estás seguro de que quieres eliminar este producto?')) return

    try {
      // Aumentar timeout para operaciones de eliminación (pueden incluir eliminación de imágenes)
      await api.delete(`/products/${productId}`, {
        timeout: 30000 // 30 segundos para operaciones de eliminación
      })
      toast.success('Producto eliminado exitosamente')
      
      // Limpiar cache del producto eliminado (usar la misma key que en loadProducts)
      const cacheKey = `products_${storeId}_${currentPage}`
      removeCache(cacheKey)
      
      loadProducts()
    } catch (error: any) {
      console.error('Error deleting product:', error)
      
      let errorMessage = 'Error al eliminar el producto'
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'La operación tardó demasiado. Por favor, intenta de nuevo o verifica tu conexión.'
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      }
      
      toast.error(errorMessage)
    }
  }

  const openEditModal = async (product: Product) => {
    setEditingProduct(product)
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id,
      stock_quantity: product.stock_quantity.toString(),
      unit: product.unit,
      is_available: product.is_available,
      is_featured: product.is_featured,
      barcode: product.barcode || ''
    })
    setImagePreview(product.image_url || '')
    
    // Cargar variantes del producto
    if (product.id) {
      try {
        const response = await api.get(`/products/${product.id}/variants`)
        if (response.data.success && response.data.data) {
          // Cargar previews de imágenes existentes
          const variantsWithPreviews = await Promise.all(
            response.data.data.map(async (v: any) => {
              let imagePreview = ''
              if (v.image_url) {
                try {
                  // Obtener URL firmada para mostrar la imagen existente
                  const signedUrlResponse = await api.get(`/upload/signed-url/${encodeURIComponent(v.image_url)}`)
                  if (signedUrlResponse.data.success && signedUrlResponse.data.data?.signedUrl) {
                    imagePreview = signedUrlResponse.data.data.signedUrl
                  }
                } catch (error) {
                  console.error('Error loading variant image preview:', error)
                }
              }
              
              return {
                id: v.id,
                variantName: v.variant_name,
                variantType: v.variant_type,
                stockQuantity: v.stock_quantity,
                sku: v.sku || '',
                imageUrl: v.image_url || '',
                imagePreview: imagePreview,
                imageFile: undefined // No hay archivo cargado inicialmente
              }
            })
          )
          
          setVariants(variantsWithPreviews)
          setShowVariantsSection(response.data.data.length > 0)
        }
      } catch (error) {
        console.error('Error loading variants:', error)
        setVariants([])
        setShowVariantsSection(false)
      }
    }
  }

  if (loading && products.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando productos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      {/* Header - Glassmorphism Style - Ocultable en scroll en pantallas pequeñas */}
      <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="py-3 sm:py-6 lg:py-8">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
              >
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
              <div className="flex-1 text-center px-2 sm:px-4 min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1 sm:mb-2 truncate">
                  Gestión de Productos
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">
                  {products.length} {products.length === 1 ? 'producto' : 'productos'}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <button
                  onClick={() => navigate(`/store/${storeId}/categories`)}
                  className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
                  title="Gestionar categorías"
                >
                  <Tag className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                </button>
                <button
                  onClick={() => {
                    if (!requireApprovedStore('crear productos')) return
                    setShowCreateModal(true)
                  }}
                  disabled={!isStoreApproved}
                  className={`px-3 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold transition-all duration-200 active:scale-95 flex items-center gap-1.5 sm:gap-2 shadow-lg sm:shadow-xl hover:shadow-xl sm:hover:shadow-2xl ${
                    isStoreApproved
                      ? 'bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Nuevo</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
        {/* Search and Filters - Glass Design - Compacto */}
        <div className="mb-4 sm:mb-6 lg:mb-8 space-y-3 sm:space-y-4">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-3 sm:left-5 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar productos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 sm:pl-14 pr-4 sm:pr-5 py-2.5 sm:py-4 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 focus:bg-white/80 transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-lg sm:shadow-xl hover:shadow-xl sm:hover:shadow-2xl"
            />
          </div>

          {/* Filters Row */}
          <div className="flex gap-2 sm:gap-4 justify-center overflow-x-auto pb-2 scrollbar-hide">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="flex-shrink-0 px-3 sm:px-5 py-2 sm:py-3 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 focus:bg-white/80 transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer"
            >
              <option value="all">Todas las categorías</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>

            <select
              value={filterAvailability}
              onChange={(e) => setFilterAvailability(e.target.value)}
              className="flex-shrink-0 px-3 sm:px-5 py-2 sm:py-3 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold text-gray-700 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 focus:bg-white/80 transition-all duration-200 shadow-lg hover:shadow-xl cursor-pointer"
            >
              <option value="all">Todos</option>
              <option value="available">Disponibles</option>
              <option value="unavailable">No disponibles</option>
            </select>
          </div>
        </div>

        {/* Products Grid - Compacto en móviles */}
        {filteredProducts.length === 0 && !loading ? (
          <div className="text-center py-8 sm:py-12 lg:py-16">
            <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-6 sm:p-10 lg:p-12 max-w-lg mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl sm:rounded-2xl mb-4 sm:mb-6 lg:mb-8 shadow-xl">
                <Package className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-blue-600" />
              </div>
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-4">No se encontraron productos</h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 font-medium">Comienza agregando tu primer producto a tu catálogo</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl transition-all duration-200 active:scale-95 text-sm sm:text-base font-semibold shadow-xl hover:shadow-2xl"
              >
                <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Agregar Primer Producto
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
            {filteredProducts.map((product) => (
              <div 
                key={product.id} 
                className="group bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 overflow-hidden hover:shadow-xl sm:hover:shadow-2xl hover:-translate-y-1 sm:hover:-translate-y-2 transition-all duration-500 hover:bg-white/80"
              >
                {/* Product Image */}
                <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
                  <ProductImage
                    imageKey={product.image_url || null}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    alt={product.name}
                  />
                  {product.is_featured && (
                    <div className="absolute top-2 sm:top-4 right-2 sm:right-4">
                      <span className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-bold bg-gradient-to-r from-yellow-400 to-amber-400 text-yellow-900 shadow-xl backdrop-blur-sm border border-yellow-300/50">
                        ⭐
                      </span>
                    </div>
                  )}
                  {!product.is_available && (
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center">
                      <span className="text-white font-bold text-xs sm:text-sm px-3 sm:px-5 py-1.5 sm:py-2.5 bg-black/60 rounded-xl sm:rounded-2xl backdrop-blur-md border border-white/20">
                        No disponible
                      </span>
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-3 sm:p-4 lg:p-6">
                  <div className="mb-2 sm:mb-4">
                    <h3 className="text-sm sm:text-base lg:text-lg xl:text-xl font-bold text-gray-900 line-clamp-2 mb-1 sm:mb-2 leading-tight">
                      {product.name}
                    </h3>
                    {product.description && (
                      <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 leading-relaxed hidden sm:block">
                        {product.description}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between mb-2 sm:mb-4 pb-2 sm:pb-4 border-b border-white/30">
                    <span className="text-base sm:text-xl lg:text-2xl xl:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      RD${(Number(product.price) || 0).toFixed(2)}
                    </span>
                    <span className="text-[10px] sm:text-xs font-semibold text-gray-600 bg-white/60 backdrop-blur-sm px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl border border-white/30">
                      {product.has_variants && product.variants && product.variants.length > 0
                        ? `${product.variants.length} var${product.variants.length > 1 ? 's' : ''}`
                        : `${product.stock_quantity} ${product.unit.substring(0, 3)}`}
                    </span>
                  </div>
                  
                  {/* Show variants if available - Oculto en móviles */}
                  {product.has_variants && product.variants && product.variants.length > 0 && (
                    <div className="mb-2 sm:mb-4 hidden sm:block">
                      <p className="text-xs font-bold text-gray-400 mb-2 uppercase tracking-wider">Variantes disponibles</p>
                      <div className="flex flex-wrap gap-2">
                        {product.variants
                          .filter(v => (v as any).is_available !== false && ((v as any).stock_quantity || v.stockQuantity || 0) > 0)
                          .slice(0, 3)
                          .map((variant) => (
                            <span
                              key={variant.id}
                              className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl text-xs font-semibold bg-blue-50/80 text-blue-700 border border-blue-200/50 backdrop-blur-sm"
                            >
                              {(variant as any).variant_name || variant.variantName}
                            </span>
                          ))}
                        {product.variants.filter(v => (v as any).is_available !== false && ((v as any).stock_quantity || v.stockQuantity || 0) > 0).length > 3 && (
                          <span className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl text-xs font-semibold bg-gray-100/80 text-gray-600 border border-gray-200/50 backdrop-blur-sm">
                            +{product.variants.filter(v => (v as any).is_available !== false && ((v as any).stock_quantity || v.stockQuantity || 0) > 0).length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {product.category && (
                    <div className="mb-2 sm:mb-4 hidden sm:block">
                      <span className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg sm:rounded-xl text-xs font-semibold bg-indigo-50/80 text-indigo-700 border border-indigo-200/50 backdrop-blur-sm">
                        {product.category.name}
                      </span>
                    </div>
                  )}

                  {/* Action Buttons - Compacto */}
                  <div className="grid grid-cols-3 gap-2 sm:gap-3">
                    <button
                      onClick={() => navigate(`/store/${storeId}/products/${product.id}`)}
                      className="flex flex-col items-center justify-center p-2 sm:p-3 lg:p-4 bg-white/60 backdrop-blur-sm border border-white/30 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-semibold text-gray-700 hover:bg-white/80 hover:shadow-lg transition-all duration-300 active:scale-95 min-h-[50px] sm:min-h-[60px] lg:min-h-[70px]"
                      title="Ver detalles"
                    >
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                      <span>Ver</span>
                    </button>
                    <button
                      onClick={() => navigate(`/store/${storeId}/products/${product.id}/edit`)}
                      className="flex flex-col items-center justify-center p-2 sm:p-3 lg:p-4 bg-blue-50/80 backdrop-blur-sm border border-blue-200/50 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-semibold text-blue-700 hover:bg-blue-100/80 hover:shadow-lg transition-all duration-300 active:scale-95 min-h-[50px] sm:min-h-[60px] lg:min-h-[70px]"
                      title="Editar producto"
                    >
                      <Edit className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                      <span>Editar</span>
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      className="flex flex-col items-center justify-center p-2 sm:p-3 lg:p-4 bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-xl sm:rounded-2xl text-[10px] sm:text-xs font-semibold text-red-700 hover:bg-red-100/80 hover:shadow-lg transition-all duration-300 active:scale-95 min-h-[50px] sm:min-h-[60px] lg:min-h-[70px]"
                      title="Eliminar producto"
                    >
                      <Trash2 className="w-4 h-4 sm:w-5 sm:h-5 mb-1 sm:mb-2" />
                      <span>Eliminar</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination - Compacto */}
        {totalPages > 1 && (
          <div className="mt-6 sm:mt-8 lg:mt-10 flex items-center justify-center gap-2 sm:gap-4">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="p-2.5 sm:p-4 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/80 hover:shadow-xl transition-all duration-300 active:scale-95 disabled:hover:bg-white/60 disabled:hover:shadow-lg shadow-lg"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <span className="text-xs sm:text-sm font-bold text-gray-700 bg-white/60 backdrop-blur-xl px-4 sm:px-6 py-2.5 sm:py-4 rounded-xl sm:rounded-2xl border border-white/30 shadow-lg">
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="p-2.5 sm:p-4 bg-white/60 backdrop-blur-xl border border-white/30 rounded-xl sm:rounded-2xl text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white/80 hover:shadow-xl transition-all duration-300 active:scale-95 disabled:hover:bg-white/60 disabled:hover:shadow-lg shadow-lg"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Product Modal */}
      {(showCreateModal || editingProduct) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl sm:rounded-xl sm:rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-gray-200/50">
            <div className="p-4 sm:p-6 md:p-8 overflow-y-auto flex-1">
              <div className="flex items-start sm:items-center justify-between mb-4 sm:mb-6 md:mb-8 pb-4 sm:pb-5 md:pb-6 border-b border-gray-200 gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-0.5 sm:mb-1">
                  {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                </h2>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {editingProduct ? 'Actualiza la información del producto' : 'Completa los datos para agregar un nuevo producto'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    setEditingProduct(null)
                    resetForm()
                  }}
                  className="p-2 sm:p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={editingProduct ? handleEditProduct : handleCreateProduct} className="space-y-4 sm:space-y-5 md:space-y-6">
                {/* Image Upload */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">
                    Imagen del Producto
                  </label>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center shadow-sm flex-shrink-0">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <Upload className="w-5 h-5 sm:w-7 sm:h-7 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="inline-flex items-center justify-center px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-100 cursor-pointer transition-all duration-200 text-xs sm:text-sm font-medium border border-gray-200 active:scale-95"
                      >
                        <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                        Subir nueva
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowImageSelector(true)}
                        className="inline-flex items-center justify-center px-3 sm:px-4 py-2 sm:py-2.5 bg-primary-50 text-primary-700 rounded-lg sm:rounded-xl hover:bg-primary-100 transition-all duration-200 text-xs sm:text-sm font-medium border border-primary-200 active:scale-95"
                      >
                        <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                        Seleccionar existente
                      </button>
                    </div>
                  </div>
                </div>

                {/* Product Name with Smart Search */}
                <div className="relative">
                  <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                    Nombre del Producto *
                    {selectedGlobalProduct && (
                      <span className="ml-1.5 sm:ml-2 text-[10px] sm:text-xs text-green-600 font-medium">
                        (Reutilizando producto existente)
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value })
                        setSelectedGlobalProduct(null)
                      }}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                      placeholder="Ej: Leche Entera"
                    />
                    {searchingProducts && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-600 border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Suggestions Dropdown */}
                  {productSuggestions.length > 0 && !selectedGlobalProduct && formData.name.length >= 2 && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto">
                      <div className="p-3 text-xs text-gray-500 font-semibold border-b border-gray-100 bg-gray-50">
                        Productos existentes - Selecciona para reutilizar
                      </div>
                      {productSuggestions.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => handleSelectProduct(product)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center gap-3">
                            {product.image_url && (
                              <div className="w-12 h-12 flex-shrink-0 bg-gray-100 rounded-xl overflow-hidden border border-gray-200">
                                <ProductImage
                                  imageKey={product.image_url}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{product.name}</p>
                              {product.description && (
                                <p className="text-xs text-gray-500 line-clamp-1">{product.description}</p>
                              )}
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">{product.unit}</span>
                                {product.store_count > 0 && (
                                  <span className="text-xs text-primary-600 font-medium">
                                    Usado en {product.store_count} {product.store_count === 1 ? 'negocio' : 'negocios'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                      <div className="p-3 border-t border-gray-100 bg-gray-50">
                        <button
                          type="button"
                          onClick={() => setProductSuggestions([])}
                          className="w-full text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Crear nuevo producto
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                    Descripción
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md resize-none"
                    placeholder="Descripción del producto..."
                  />
                </div>

                {/* Category and Price Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                      Categoría *
                    </label>
                    <select
                      required
                      value={formData.category_id}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
                    >
                      <option value="">Seleccionar categoría...</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                      Precio *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Stock and Unit Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                      Stock *
                    </label>
                    <input
                      type="number"
                      required
                      value={formData.stock_quantity}
                      onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                      Unidad
                    </label>
                    <select
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 shadow-sm hover:shadow-md cursor-pointer"
                    >
                      <option value="unidad">Unidad</option>
                      <option value="kg">Kilogramo</option>
                      <option value="lb">Libra</option>
                      <option value="l">Litro</option>
                      <option value="ml">Mililitro</option>
                      <option value="paquete">Paquete</option>
                      <option value="caja">Caja</option>
                    </select>
                  </div>
                </div>

                {/* Código de Barras */}
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2">
                    Código de Barras
                  </label>
                  <input
                    type="text"
                    value={formData.barcode}
                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                    placeholder="Ej: 7802950002171"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Ingresa el código de barras del producto (EAN, UPC, etc.)
                  </p>
                </div>

                {/* Options */}
                <div className="space-y-2 sm:space-y-3 p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl border border-gray-200">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_available"
                      checked={formData.is_available}
                      onChange={(e) => setFormData({ ...formData, is_available: e.target.checked })}
                      className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="is_available" className="ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer">
                      Producto disponible
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                    />
                    <label htmlFor="is_featured" className="ml-2 sm:ml-3 text-xs sm:text-sm font-medium text-gray-700 cursor-pointer">
                      Producto destacado
                    </label>
                  </div>
                </div>

                {/* Variants Section */}
                <div className="border-t pt-3 sm:pt-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
                    <div className="flex-1">
                      <label className="text-xs sm:text-sm font-medium text-gray-700">
                        Variantes del Producto
                      </label>
                      <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                        Agrega sabores, colores, tamaños, etc. con stock independiente
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowVariantsSection(!showVariantsSection)
                        if (!showVariantsSection && variants.length === 0) {
                          addVariant()
                        }
                      }}
                      className="px-3 py-1.5 text-xs sm:text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap"
                    >
                      {showVariantsSection ? 'Ocultar' : 'Agregar Variantes'}
                    </button>
                  </div>

                  {showVariantsSection && (
                    <div className="space-y-3 sm:space-y-4 max-h-80 sm:max-h-96 overflow-y-auto">
                      {variants.map((variant, index) => (
                        <div key={index} className="p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl border border-gray-200">
                          <div className="flex items-center justify-between mb-2 sm:mb-3">
                            <h4 className="text-xs sm:text-sm font-semibold text-gray-700">
                              Variante {index + 1}
                            </h4>
                            <button
                              type="button"
                              onClick={() => removeVariant(index)}
                              className="text-red-600 hover:text-red-700 p-1"
                            >
                              <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div>
                              <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                                Nombre *
                              </label>
                              <input
                                type="text"
                                required
                                value={variant.variantName}
                                onChange={(e) => updateVariant(index, 'variantName', e.target.value)}
                                className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                placeholder="Ej: Cola, Naranja, Azul"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                                Tipo *
                              </label>
                              <select
                                required
                                value={variant.variantType}
                                onChange={(e) => updateVariant(index, 'variantType', e.target.value as any)}
                                className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              >
                                <option value="flavor">Sabor</option>
                                <option value="color">Color</option>
                                <option value="size">Tamaño</option>
                                <option value="other">Otro</option>
                              </select>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
                            <div>
                              <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                                Stock *
                              </label>
                              <input
                                type="number"
                                required
                                min="0"
                                value={variant.stockQuantity}
                                onChange={(e) => updateVariant(index, 'stockQuantity', parseInt(e.target.value) || 0)}
                                className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] sm:text-xs font-medium text-gray-700 mb-1">
                                SKU
                              </label>
                              <input
                                type="text"
                                value={variant.sku || ''}
                                onChange={(e) => updateVariant(index, 'sku', e.target.value)}
                                className="w-full px-2 sm:px-2.5 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                                placeholder="Opcional"
                              />
                            </div>
                          </div>

                          {/* Variant Image */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Imagen de la Variante (Opcional)
                            </label>
                            <div className="flex items-center space-x-3">
                              <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 flex items-center justify-center">
                                {variant.imagePreview ? (
                                  <img src={variant.imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                  <Upload className="w-4 h-4 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0]
                                    if (file) handleVariantImageSelect(index, file)
                                  }}
                                  className="hidden"
                                  id={`variant-image-${index}`}
                                />
                                <label
                                  htmlFor={`variant-image-${index}`}
                                  className="inline-flex items-center px-2 py-1.5 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors text-xs"
                                >
                                  <Upload className="w-3 h-3 mr-1" />
                                  Subir nueva
                                </label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setVariantImageSelectorIndex(index)
                                    setShowImageSelector(true)
                                  }}
                                  className="inline-flex items-center px-2 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-xs"
                                >
                                  <Search className="w-3 h-3 mr-1" />
                                  Existente
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addVariant}
                        className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors text-sm font-medium"
                      >
                        <Plus className="w-4 h-4 inline mr-2" />
                        Agregar Otra Variante
                      </button>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false)
                      setEditingProduct(null)
                      resetForm()
                    }}
                    className="flex-1 py-2.5 sm:py-3 md:py-3.5 px-4 bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-200 transition-all duration-200 active:scale-95 font-medium border border-gray-200 text-sm sm:text-base"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={creatingProduct}
                    className="flex-1 py-2.5 sm:py-3 md:py-3.5 px-4 bg-primary-600 text-white rounded-lg sm:rounded-xl hover:bg-primary-700 transition-all duration-200 active:scale-95 font-semibold shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {creatingProduct ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {editingProduct ? 'Actualizando...' : 'Creando...'}
                      </>
                    ) : (
                      `${editingProduct ? 'Actualizar' : 'Crear'} Producto`
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Image Selector Modal */}
      {showImageSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl sm:rounded-xl sm:rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-gray-200/50">
            <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200">
              <div className="flex items-start sm:items-center justify-between mb-3 sm:mb-4 gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900 mb-0.5 sm:mb-1">Seleccionar Imagen Existente</h2>
                  <p className="text-xs sm:text-sm text-gray-500">Busca y selecciona una imagen de tu galería</p>
                </div>
                <button
                  onClick={() => {
                    setShowImageSelector(false)
                    setImageSearchTerm('')
                  }}
                  className="p-2 sm:p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0"
                >
                  <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                </button>
              </div>
              <div>
                <div className="relative">
                  <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  value={imageSearchTerm}
                  onChange={(e) => setImageSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre de archivo..."
                    className="w-full pl-10 sm:pl-12 pr-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-sm sm:text-base text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                />
              </div>
            </div>
            </div>
            <div className="p-3 sm:p-4 md:p-6 overflow-y-auto flex-1 bg-gray-50/50">
              {loadingImages ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent"></div>
                </div>
              ) : availableImages.length === 0 ? (
                <div className="text-center py-20">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gray-100 mb-4">
                    <Search className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No se encontraron imágenes</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                  {availableImages.map((image) => (
                    <button
                      key={image.key}
                      type="button"
                      onClick={() => {
                        if (variantImageSelectorIndex !== null) {
                          handleSelectExistingVariantImage(image.key, image.signedUrl)
                        } else {
                          handleSelectExistingImage(image.key, image.signedUrl)
                        }
                      }}
                      className="group relative aspect-square bg-gray-100 rounded-xl sm:rounded-2xl overflow-hidden border-2 border-transparent hover:border-primary-500 transition-all duration-200 hover:shadow-lg hover:scale-105"
                    >
                      <img
                        src={image.signedUrl}
                        alt={image.key}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs text-center px-2 font-medium">
                          {image.key.split('/').pop()}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  )
}

export default StoreProducts
